// Signage → Cupones (/signage/cupones). Cupones canjeables pantalla→caja: código
// corto con vigencia POR HORA, tope de canjes, alcance por local y descuento en %
// o $ (opcionalmente atado a un producto). La escena `cupon` los exhibe y
// pos_registrar_venta los valida/canjea atómicamente — atribución directa
// pantalla→venta (reporte al pie).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, Pencil, Plus, RefreshCw, Search, TicketPercent, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Campo, Modal, Switch, fieldCls } from '@/features/pos/shared';
import { LOCALE } from '@/lib/utils/regional';

const POS = { headers: { 'Accept-Profile': 'pos', 'Content-Profile': 'pos' } };

interface Cupon {
  id: string; codigo: string; nombre: string; descripcion: string | null;
  descuento_pct: number | null; descuento_monto: number | null; producto_id: string | null;
  vigente_desde: string; vigente_hasta: string; max_canjes: number | null; canjes: number;
  location_id: string | null; en_pantalla: boolean; es_activo: boolean;
}
interface ProdRow { id: string; codigo: string; nombre: string; precio_base: number }
interface LocRow { id: string; nombre: string }
interface ReporteRow {
  id: string; codigo: string; nombre: string; canjes_total: number; canjes_periodo: number;
  descuento_otorgado: number; ventas_generadas: number;
  por_local: { local: string | null; canjes: number; ventas: number }[];
}

const money = (n: number) => `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
// timestamptz → valor de <input type=datetime-local> (hora local) y viceversa
const aLocal = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const aIso = (v: string) => (v ? new Date(v).toISOString() : null);
const genCodigo = () => {
  const abc = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';   // sin O/0/I/1 (se dictan en voz alta)
  return Array.from(crypto.getRandomValues(new Uint8Array(6)), (b) => abc[b % abc.length]).join('');
};

const vacio = {
  codigo: '', nombre: '', descripcion: '', tipo: 'pct' as 'pct' | 'monto', valor: 10,
  producto_id: '', producto_nombre: '', vigente_desde: aLocal(new Date().toISOString()),
  vigente_hasta: '', max_canjes: '', location_id: '', en_pantalla: true, es_activo: true,
};

export function Cupones() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: cupones = [], isFetching } = useQuery<Cupon[]>({
    queryKey: ['sig-cupones'],
    queryFn: async () => (await apiClient.get<Cupon[]>('/rest/pos.cupones?select=*&deleted_at=is.null&order=vigente_hasta.desc', POS)).data,
  });
  const { data: locales = [] } = useQuery<LocRow[]>({
    queryKey: ['sig-cupon-locs'],
    queryFn: async () => (await apiClient.get<LocRow[]>('/rest/inventario.locations?select=id,nombre&deleted_at=is.null&order=nombre')).data,
    staleTime: 5 * 60 * 1000,
  });
  const { data: reporte = [] } = useQuery<ReporteRow[]>({
    queryKey: ['sig-cupon-reporte'],
    queryFn: async () => (await apiClient.post<ReporteRow[]>('/rpc/cupon_reporte', {}, POS)).data,
  });

  const [abierto, setAbierto] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(vacio);
  const [busq, setBusq] = useState('');

  const { data: resultados = [] } = useQuery<ProdRow[]>({
    queryKey: ['sig-cupon-prodsearch', busq],
    enabled: abierto && busq.trim().length >= 2,
    queryFn: async () => (await apiClient.get<ProdRow[]>(
      `/rest/inventario.productos?select=id,codigo,nombre,precio_base&es_activo=eq.true&deleted_at=is.null&precio_base=gt.0&nombre=ilike.*${encodeURIComponent(busq.trim())}*&order=nombre&limit=20`)).data,
    staleTime: 30_000,
  });

  const abrir = (c?: Cupon) => {
    setBusq('');
    if (c) {
      setEditId(c.id);
      setForm({
        codigo: c.codigo, nombre: c.nombre, descripcion: c.descripcion ?? '',
        tipo: c.descuento_pct != null ? 'pct' : 'monto', valor: Number(c.descuento_pct ?? c.descuento_monto ?? 0),
        producto_id: c.producto_id ?? '', producto_nombre: '',
        vigente_desde: aLocal(c.vigente_desde), vigente_hasta: aLocal(c.vigente_hasta),
        max_canjes: c.max_canjes != null ? String(c.max_canjes) : '', location_id: c.location_id ?? '',
        en_pantalla: c.en_pantalla, es_activo: c.es_activo,
      });
      if (c.producto_id) {
        void apiClient.get<ProdRow[]>(`/rest/inventario.productos?select=id,codigo,nombre,precio_base&id=eq.${c.producto_id}`)
          .then((r) => setForm((f) => ({ ...f, producto_nombre: r.data[0]?.nombre ?? '' })));
      }
    } else { setEditId(null); setForm({ ...vacio, codigo: genCodigo(), vigente_desde: aLocal(new Date().toISOString()) }); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      if (!form.codigo.trim()) throw new Error('El código es obligatorio');
      if (!form.nombre.trim()) throw new Error('El nombre es obligatorio');
      if (!(Number(form.valor) > 0)) throw new Error('El descuento debe ser mayor a 0');
      if (form.tipo === 'pct' && Number(form.valor) > 100) throw new Error('El % no puede superar 100');
      if (!form.vigente_hasta) throw new Error('Indica hasta cuándo es válido (con hora)');
      const body = {
        codigo: form.codigo.trim().toUpperCase(), nombre: form.nombre.trim(), descripcion: form.descripcion || null,
        descuento_pct: form.tipo === 'pct' ? Number(form.valor) : null,
        descuento_monto: form.tipo === 'monto' ? Number(form.valor) : null,
        producto_id: form.producto_id || null,
        vigente_desde: aIso(form.vigente_desde) ?? new Date().toISOString(), vigente_hasta: aIso(form.vigente_hasta),
        max_canjes: form.max_canjes ? Number(form.max_canjes) : null,
        location_id: form.location_id || null, en_pantalla: form.en_pantalla, es_activo: form.es_activo,
      };
      if (editId) await apiClient.patch(`/rest/pos.cupones?id=eq.${editId}`, body, POS);
      else await apiClient.post('/rest/pos.cupones', body, POS);
    },
    onSuccess: () => { toast.success('Cupón guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['sig-cupones'] }); void qc.invalidateQueries({ queryKey: ['sig-cupon-reporte'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => apiClient.patch(`/rest/pos.cupones?id=eq.${id}`, { deleted_at: new Date().toISOString(), es_activo: false }, POS),
    onSuccess: () => { toast.success('Cupón eliminado'); void qc.invalidateQueries({ queryKey: ['sig-cupones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const vigente = (c: Cupon) => {
    const t = Date.now();
    return c.es_activo && t >= new Date(c.vigente_desde).getTime() && t <= new Date(c.vigente_hasta).getTime()
      && (c.max_canjes == null || c.canjes < c.max_canjes);
  };
  const fmtVig = (c: Cupon) => `${new Date(c.vigente_desde).toLocaleString(LOCALE, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} → ${new Date(c.vigente_hasta).toLocaleString(LOCALE, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`;
  const nombreLocal = (id: string | null) => (id ? (locales.find((l) => l.id === id)?.nombre ?? '—') : 'Todos');

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      {dlg.ui}
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><TicketPercent className="w-5 h-5" /> Signage — Cupones</h1>
        <Button className="ml-auto" onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo cupón</Button>
      </div>
      <p className="text-sm text-muted-foreground">
        La pantalla exhibe el código; el cliente lo dice en caja y el POS lo valida y canjea al cobrar
        (vigencia con hora, local y tope de canjes). El descuento que promete la pantalla es el que cobra la caja.
      </p>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Cupones {isFetching && <RefreshCw className="inline w-3.5 h-3.5 animate-spin ml-1" />}</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="text-muted-foreground border-b">
              <th className="py-1.5 pr-3 text-left">Código</th><th className="py-1.5 pr-3 text-left">Nombre</th>
              <th className="py-1.5 pr-3 text-right">Descuento</th><th className="py-1.5 pr-3 text-left">Vigencia</th>
              <th className="py-1.5 pr-3 text-left">Local</th><th className="py-1.5 pr-3 text-right">Canjes</th>
              <th className="py-1.5 pr-3 text-center">Pantalla</th><th className="py-1.5 text-right">Acciones</th>
            </tr></thead>
            <tbody>
              {cupones.map((c) => (
                <tr key={c.id} className={`border-b last:border-0 ${vigente(c) ? '' : 'opacity-50'}`}>
                  <td className="py-1.5 pr-3 font-mono font-semibold">{c.codigo}</td>
                  <td className="py-1.5 pr-3">{c.nombre}{c.producto_id && <span className="ml-1 text-xs text-muted-foreground">(producto)</span>}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{c.descuento_pct != null ? `${Number(c.descuento_pct)}%` : money(Number(c.descuento_monto))}</td>
                  <td className="py-1.5 pr-3 text-xs text-muted-foreground">{fmtVig(c)}</td>
                  <td className="py-1.5 pr-3">{nombreLocal(c.location_id)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{c.canjes}{c.max_canjes != null ? ` / ${c.max_canjes}` : ''}</td>
                  <td className="py-1.5 pr-3 text-center">{c.en_pantalla ? '✓' : '—'}</td>
                  <td className="py-1.5 text-right">
                    <button className="p-1 text-muted-foreground hover:text-foreground" onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                    <button className="p-1 text-red-500 hover:text-red-600" onClick={async () => { if (await dlg.confirmar(`¿Eliminar el cupón ${c.codigo}?`, { peligro: true })) eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {!cupones.length && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Sin cupones. Crea el primero y agrégalo a una plantilla con la escena «Cupón».</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Canjes de los últimos 30 días (atribución pantalla→venta)</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="text-muted-foreground border-b">
              <th className="py-1.5 pr-3 text-left">Cupón</th><th className="py-1.5 pr-3 text-right">Canjes</th>
              <th className="py-1.5 pr-3 text-right">Descuento otorgado</th><th className="py-1.5 pr-3 text-right">Ventas generadas</th>
              <th className="py-1.5 text-left">Por local</th>
            </tr></thead>
            <tbody>
              {reporte.filter((r) => r.canjes_periodo > 0).map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="py-1.5 pr-3"><span className="font-mono font-semibold">{r.codigo}</span> · {r.nombre}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{r.canjes_periodo}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{money(Number(r.descuento_otorgado))}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums font-medium">{money(Number(r.ventas_generadas))}</td>
                  <td className="py-1.5 text-xs text-muted-foreground">{r.por_local.map((l) => `${l.local ?? '—'}: ${l.canjes} (${money(Number(l.ventas))})`).join(' · ')}</td>
                </tr>
              ))}
              {!reporte.some((r) => r.canjes_periodo > 0) && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Aún no hay canjes en el período.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar cupón' : 'Nuevo cupón'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
              <Campo label="Código (lo dice el cliente en caja)">
                <input className={`${fieldCls} font-mono uppercase`} maxLength={24} value={form.codigo}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })} />
              </Campo>
              <Button variant="outline" onClick={() => setForm({ ...form, codigo: genCodigo() })}>Generar</Button>
            </div>
            <Campo label="Nombre (aparece en la pantalla)">
              <input className={fieldCls} maxLength={160} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="10% en tu compra de hoy" />
            </Campo>
            <Campo label="Descripción (opcional)">
              <input className={fieldCls} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
            </Campo>
            <div className="grid grid-cols-2 gap-2">
              <Campo label="Tipo de descuento">
                <select className={fieldCls} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value as 'pct' | 'monto' })}>
                  <option value="pct">% sobre la venta / producto</option>
                  <option value="monto">$ fijos sobre la venta</option>
                </select>
              </Campo>
              <Campo label={form.tipo === 'pct' ? 'Porcentaje (%)' : 'Monto ($)'}>
                <input type="number" min={0.01} step={form.tipo === 'pct' ? 1 : 0.01} className={`${fieldCls} text-right`} value={form.valor}
                  onChange={(e) => setForm({ ...form, valor: Number(e.target.value) || 0 })} />
              </Campo>
            </div>
            <Campo label="Producto (opcional — el % aplica solo a él; con $ fijos, exige que esté en la venta)">
              {form.producto_id ? (
                <div className="flex items-center gap-2 text-sm">
                  <span className="flex-1 truncate">{form.producto_nombre || form.producto_id}</span>
                  <button className="text-red-500 text-xs hover:underline" onClick={() => setForm({ ...form, producto_id: '', producto_nombre: '' })}>quitar</button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
                  <input className={`${fieldCls} pl-8`} placeholder="Buscar producto…" value={busq} onChange={(e) => setBusq(e.target.value)} />
                  {busq.trim().length >= 2 && resultados.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border bg-background shadow-lg max-h-48 overflow-auto">
                      {resultados.map((p) => (
                        <button key={p.id} className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
                          onClick={() => { setForm({ ...form, producto_id: p.id, producto_nombre: p.nombre }); setBusq(''); }}>
                          {p.nombre} <span className="text-xs text-muted-foreground">· {p.codigo} · {money(Number(p.precio_base))}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Campo>
            <div className="grid grid-cols-2 gap-2">
              <Campo label="Vigente desde"><input type="datetime-local" className={fieldCls} value={form.vigente_desde} onChange={(e) => setForm({ ...form, vigente_desde: e.target.value })} /></Campo>
              <Campo label="Vigente hasta (con hora)"><input type="datetime-local" className={fieldCls} value={form.vigente_hasta} onChange={(e) => setForm({ ...form, vigente_hasta: e.target.value })} /></Campo>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Campo label="Tope de canjes (vacío = ilimitado)">
                <input type="number" min={1} className={`${fieldCls} text-right`} value={form.max_canjes} onChange={(e) => setForm({ ...form, max_canjes: e.target.value })} placeholder="p. ej. 50" />
              </Campo>
              <Campo label="Local (vacío = todos)">
                <select className={fieldCls} value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
                  <option value="">Todos los locales</option>
                  {locales.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                </select>
              </Campo>
            </div>
            <div className="flex items-center gap-6">
              <Switch checked={form.en_pantalla} onChange={(v) => setForm({ ...form, en_pantalla: v })} label="Exhibir en pantallas" />
              <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
            </div>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
